import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { NextArrow, PrevArrow } from './SliderArrowButtons.jsx';
import ResponseErrorMessage from './ResponseErrorMessage.jsx';
import CarouselImage from './CarouselImage.jsx';
import ResponseEmptyMessageMessage from './ResponseEmptyMessage.jsx';
import LoadingLineSkeleton from '../skeletons/LoadingLineSkeleton.jsx';

/**
 * Standard deBouncer
 *
 * used to slow or throttle an event as needed
 *
 * @param fn
 * @param ms
 * @returns {(function(): void)|*}
 */
const debounce = function(fn, ms) {
	let timer;

	return () => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			timer = null;
			fn.apply(this, arguments);
		}, ms);
	};
};

/**
 * React-Slick Carousel
 *
 * Receives:
 * options for the slider,
 * loading, error and response state
 * title is optional
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const ProductCarousel = props => {
	const { options, data, error, loading, title, alt } = props;
	const [innerWidth, setInnerWidth] = React.useState(window.innerWidth);
	const [pageIndex, setPageIndex] = useState(0);
	const [prevVisible, setPrevVisible] = useState(false);
	const [nextVisible, setNextVisible] = useState(false);
	const [mouseDownObj, setMouseDownObj] = useState(1000);
	const slider = useRef();

	// Set the window resize listener to the deBouncer so React slows it s reaction to this event
	const debouncedHandleResize = debounce(()=> setInnerWidth(window.innerWidth), 800);

	/**
	 * Displays the carousel next and previous buttons when the index and closing index slides are available
	 *
	 * @param closingIndex
	 */
	const carouselNavigationButton = closingIndex => {
		if (pageIndex === 0) {
			setPrevVisible(false);
			setNextVisible(true);
		} else if (pageIndex > 0 && pageIndex < closingIndex) {
			setPrevVisible(true);
			setNextVisible(true);
		} else {
			setPrevVisible(true);
			setNextVisible(false);
		}
	};

	/**
	 * sliderHandler method helps with the responsiveness of the carousel component when the browser gets resized.
	 *
	 * Important: The pagCount deductions number should match the option values for the carousel
	 * to prevent unexpected results
	 *
	 */
	const sliderHandler = function() {
		const slideCount = slider.current.innerSlider.state.slideCount;

		if (innerWidth > 1089) {
			carouselNavigationButton(slideCount - 7);
		}

		if (innerWidth >= 768 && innerWidth <= 1089) {
			carouselNavigationButton(slideCount - 3);
		}

		if (innerWidth < 768) {
			carouselNavigationButton(slideCount - 2);
		}
	};

	/**
	 * Creates a generic template slider
	 *
	 * @returns {unknown[]}
	 */
	const getProducts = () => {
		return data.map((value, key) => {
			return (
				<a key={key} href={value.url} data-uuid={value.uuid}
					   className={'product-box' + (mouseDownObj === key ? ' mouse-down' : '')}
					   onMouseDown={()=>setMouseDownObj(key)} >
					<div className={'product-area'}>
						<p>{ value.title }</p>
						<CarouselImage src={value.image} alt={alt + ': ' + value.title} />
					</div>
				</a>
			);
		});
	};

	// Additional features to add into the slider options
	const features = {
		nextArrow: <NextArrow classLabel={'next-arrow-pad'} visible={nextVisible} />,
		prevArrow: <PrevArrow classLabel={'prev-arrow-pad'} visible={prevVisible} />,
		beforeChange: (currentSlide, nextSlide) => setPageIndex(nextSlide)
	};

	// Combine the options with the features
	const settings = { ...options, ...features };

	/**
	 * Generate the product slides, may return a notification if product list is empty
	 *
	 * @returns {JSX.Element}
	 */
	const getSlider = () => {
		let productsSlides;

		if (!data.length) { // if data array is empty
			productsSlides = <ResponseEmptyMessageMessage />;
		} else {
			productsSlides = <Slider ref={slider} {...settings}> { getProducts() }</Slider>;
		}
		return productsSlides;
	};

	/**
	 * Listens for innerWidth, pageIndex, and data state changes to send the slider to the pageIndex slide
	 */
	useEffect(() => {
		if (data && (typeof slider.current !== 'undefined' && slider.current.innerSlider !== null)) {
			slider.current.slickGoTo(pageIndex);
			sliderHandler();
		}
	}, [innerWidth, pageIndex, data]);

	/**
	 * Run once to add a resize event listener
	 */
	useEffect(() => {
		window.addEventListener('resize', debouncedHandleResize);
		return () => {
			window.removeEventListener('resize', debouncedHandleResize);
		};
	}, []);

	return (
		<div className={'product-wrapper row'}>
			<div className={'my-products col-11 col-offset-1'}>
				{ typeof title !== 'undefined' && <h2 className={'title-line artz'}>{ title }</h2> }
				{ loading && <LoadingLineSkeleton options={options} /> }
				{ error !== null && <ResponseErrorMessage error={error} /> }
				{ data && getSlider() }
			</div>
		</div>
	);
};

ProductCarousel.propTypes = {
	title: PropTypes.object,
	options: PropTypes.object,
	data: PropTypes.array,
	loading: PropTypes.bool,
	error: PropTypes.object,
	alt: PropTypes.string
};

export default ProductCarousel;
